import { makeStyles } from '@map/theme';
import { SvgIconProps } from '@mui/material';
import { ComponentType } from 'react';
import { IconProps } from './types';

const useStyles = makeStyles(
  (
    { borderRadius, palette },
    {
      filledColor,
      filledBackground,
    }: { filledColor: string | undefined; filledBackground: string | undefined },
  ) => ({
    // The filled and outlined variants reduce the shape size to 2/3.
    // paddings are calculated as: [(24px - 16px) / 2 = 4px] part of 24px = 1/6
    variantFilled: {
      boxSizing: 'border-box',
      padding: `${1 / 6}em`,
      borderRadius: borderRadius('circle'),
      background: filledBackground || 'currentColor',
      '& > path': {
        fill: filledColor || palette.common.white,
      },
    },
    variantOutlined: {
      boxSizing: 'border-box',
      padding: `${1 / 6}em`,
      borderRadius: borderRadius('circle'),
      // borders round up to whole pixels, so shadow is used instead
      // width should be same as the stroke width at 16px, but zoomed out at 24px
      boxShadow: `0 0 0 ${1 / 14}em currentColor inset`,
    },
  }),
);

export type IconVariantProps = Either<
  {
    variant?: 'filled';
    filledColor?: string;
    filledBackground?: string;
  },
  {
    variant?: 'normal' | 'outlined';
  }
>;

type Props = SvgIconProps & IconVariantProps & { Component: ComponentType<IconProps> };

const Icon = ({
  Component,
  variant,
  className,
  filledColor,
  filledBackground,
  ...props
}: Props) => {
  const { classes, cx } = useStyles({ filledColor, filledBackground });
  return (
    <Component
      className={cx(
        variant === 'filled' && classes.variantFilled,
        variant === 'outlined' && classes.variantOutlined,
        className,
      )}
      {...props}
      intrinsicSize={variant === 'normal' && props.fontSize !== 'small' ? 24 : 16}
    />
  );
};

export default Icon;
